import { useMemo, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { postQuickRequestComment, postQuoteRequestComment } from "api";
import { quickRequestQueryKeys } from "api/queries/quick-request/quickRequestQueryKeys";
import { useGetQuickRequestComments } from "api/queries/quick-request/useGetQuickRequestComments";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { useGetQuoteRequestComments } from "api/queries/quotes/useGetQuoteRequestComments";
import { useGetProviderOptions } from "api/queries/quotes/useGetProviderOptions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { isInternalUsersOnlyCommentAudience } from "constants/QuoteRequestStatus";
import CommonComments from "pages/common/ShipmentQuotesV2/components/Comments";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import useFileHook from "hooks/useFileHook";

interface CommentsProps {
  isQuickRequest?: boolean;
}

function Comments({ isQuickRequest }: CommentsProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const shipmentId = id!;

  const { permissions } = usePermissions();
  const { company_id } = JSON.parse(permissions ?? "{}");

  const methods = useForm({
    defaultValues: {
      selected_message_audience: "all",
      comment_message: "",
    },
  });
  const { getValues, setValue } = methods;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { data: quoteComments, isInitialLoading: quoteCommentsLoading } = useGetQuoteRequestComments(shipmentId, {
    enabled: !isQuickRequest && !!id,
  });
  const { data: quickComments, isInitialLoading: quickCommentsLoading } = useGetQuickRequestComments(shipmentId, {
    enabled: isQuickRequest && !!id,
  });
  const comments: any = useMemo(
    () => (isQuickRequest ? quickComments : quoteComments) || {},
    [isQuickRequest, quickComments, quoteComments],
  );
  const isLoadingComment = isQuickRequest ? quickCommentsLoading : quoteCommentsLoading;

  const { data: shipmentData } = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });

  const isInternalUsersOnly = useMemo(
    () => !isQuickRequest && isInternalUsersOnlyCommentAudience(shipmentData?.organization?.request_status),
    [isQuickRequest, shipmentData?.organization?.request_status],
  );

  useEffect(() => {
    if (isInternalUsersOnly && company_id) {
      setValue("selected_message_audience", company_id);
    }
  }, [isInternalUsersOnly, company_id, setValue]);

  const { data: providers } = useGetProviderOptions({
    shipmentId,
    options: { enabled: !isQuickRequest },
  });

  const invitedProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter((provider: any) => provider.status !== "not_invited");
  }, [providers]);

  const audienceOptions = [
    {
      value: "all",
      label: intl.formatMessage({ id: "quote-request.comments.everyone", defaultMessage: "Everyone" }),
      sublabel: intl.formatMessage({
        id: "quote-request.comments.everyone.sublabel",
        defaultMessage: "Anyone who has access to this shipment",
      }),
    },
    {
      value: company_id,
      label: intl.formatMessage({ id: "quote-request.comments.internalUsers", defaultMessage: "Internal users" }),
      sublabel: intl.formatMessage({
        id: "quote-request.comments.internalUsers.sublabel",
        defaultMessage: "Only people from your company",
      }),
    },
    ...invitedProviders.map((provider: any) => ({
      value: provider.id,
      label: provider.company_name,
      group: intl.formatMessage({ id: "settings.providers", defaultMessage: "Providers" }),
    })),
  ];

  const fileProps = useFileHook({ maxFileCount: 10 });
  const { resetFiles, formattedFiles } = fileProps;

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: () => {
      const payload = {
        comment: textAreaRef.current?.value,
        ...((!isQuickRequest && {
          audience: isInternalUsersOnly ? company_id : getValues("selected_message_audience"),
        }) as {}),
        ...(formattedFiles?.length > 0 && {
          attachment_references: formattedFiles.map((file) => file.reference_number),
        }),
      };
      return isQuickRequest ? postQuickRequestComment(id, payload) : postQuoteRequestComment(id, payload);
    },
    onSuccess: () => {
      setValue("comment_message", "");
      textAreaRef.current!.value = "";
      queryClient.invalidateQueries({
        queryKey: isQuickRequest
          ? quickRequestQueryKeys.quickRequestCommentsListing(shipmentId)
          : quoteRequestQueryKeys.quoteCommentsListing(shipmentId),
      });
      resetFiles();
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  return (
    <CommonComments
      methods={methods}
      audienceOptions={audienceOptions}
      textAreaRef={textAreaRef}
      onSubmitComment={() => mutate()}
      isSubmitting={isSubmitting || isLoadingComment}
      comments={comments?.results}
      fileProps={fileProps}
      isQuickRequest={isQuickRequest}
      isInternalUsersOnly={isInternalUsersOnly}
    />
  );
}

export default Comments;

Comments.defaultProps = {
  isQuickRequest: false,
};
